/**
 * COL-246 — "Complete your CoLiviq account" dialog.
 *
 * Shown after a logged-out applicant fills the room application form. Name and
 * email are carried over from the application (read-only); the applicant only
 * picks a password. On submit the parent creates the account + application via
 * the `create-application-account` edge function.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { BrandWordmark } from "@/components/BrandWordmark";

interface CompleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  email: string;
  loading: boolean;
  error?: string | null;
  onSubmit: (password: string) => void;
}

export const CompleteAccountDialog = ({
  open,
  onOpenChange,
  name,
  email,
  loading,
  error,
  onSubmit,
}: CompleteAccountDialogProps) => {
  const { t } = useLanguage();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < 6) {
      setLocalError(
        t(
          "auth.completeAccount.errPasswordTooShort",
          "Password must be at least 6 characters."
        )
      );
      return;
    }
    if (password !== confirm) {
      setLocalError(
        t("auth.completeAccount.errPasswordMismatch", "Passwords don't match.")
      );
      return;
    }
    onSubmit(password);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <BrandWordmark className="h-8 w-auto" />
          </div>
          <DialogTitle className="text-2xl font-bold text-center">
            {t("auth.completeAccount.title", "Complete your account")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t(
              "auth.completeAccount.description",
              "Thanks for applying! Set a password to finish creating your coliviq account."
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ca-name">
              {t("auth.completeAccount.name", "Name")}
            </Label>
            <Input id="ca-name" value={name} readOnly disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-email">
              {t("auth.completeAccount.email", "Email")}
            </Label>
            <Input id="ca-email" type="email" value={email} readOnly disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-password">
              {t("auth.completeAccount.password", "Password")}
            </Label>
            <Input
              id="ca-password"
              type="password"
              placeholder="••••••••"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-confirm">
              {t("auth.completeAccount.confirmPassword", "Confirm password")}
            </Label>
            <Input
              id="ca-confirm"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
            />
          </div>

          {(localError || error) && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {localError || error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading
              ? t("auth.completeAccount.creating", "Creating account…")
              : t("auth.completeAccount.submit", "Create account & submit")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {t(
              "auth.completeAccount.confirmEmailHint",
              "You'll get an email to confirm your account before your dashboard unlocks."
            )}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
